/**
 * A MobileSubmenu is an element representing a submenu for our mobile menu.
 */

import {memo, useRef} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/sharp-regular-svg-icons/faArrowLeft";
import MobileMenuItem from "./MobileMenuItem";
import {CollapsibleMenu} from "./MenuTypes";
import useArrowKeyNavigation from "./useArrowKeyNavigation";
import useEnterExitAnimation from "../useEnterExitAnimation";

interface MobileSubmenuProps extends CollapsibleMenu {
	className?: string;
	expandedItem?: string;
	key?: string;
	openSubmenu: (submenu: CollapsibleMenu) => void;
	onClose?: () => void;
}

const MobileSubmenu: React.FC<MobileSubmenuProps> = ({...props}) => {
	/**
	 * setup ref so we can focus first item in list.
	 */
	const firstItem = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
	const handleUpDownEvent = useArrowKeyNavigation();

	const [rootAnimation] = useEnterExitAnimation({
		enterAnimation: [{opacity: 1}, {duration: 0.3}],
		exitAnimation: [{opacity: 0}, {duration: 0.3}],
		onEnterTriggered: () => {
			firstItem?.current?.focus();
		},
	});

	const [backAnimation] = useEnterExitAnimation({
		enterAnimation: [{top: 0}, {duration: 0.3}],
		exitAnimation: [{top: "-0.5rem"}, {duration: 0.3}],
	});

	const [itemAnimation] = useEnterExitAnimation({
		enterAnimation: [{paddingLeft: "1rem"}, {duration: 0.3}],
		exitAnimation: [{paddingLeft: "0"}, {duration: 0.3}],
	});

	// focus management
	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		switch (e.key) {
			case "ArrowLeft":
				props.onClose?.();
				break;
		}
	};

	// so we can use aria-labelledby
	const submenuLabelId = `${props.id}-submenu-label`;

	// animation stuff

	return (
		<div
			ref={rootAnimation}
			aria-labelledby={submenuLabelId}
			className="absolute left-0 top-0 flex h-full w-full flex-col bg-green-800 opacity-0 md:items-center"
			onKeyDown={onKeyDown}
		>
			<button
				onClick={() => {
					props.onClose?.();
				}}
				onMouseEnter={(e) => {
					(e.target as HTMLButtonElement).focus();
				}}
				onKeyDown={(e) => {
					handleUpDownEvent(e);
				}}
				ref={backAnimation}
				className="relative top-[-0.5rem] flex h-12 items-center gap-2 rounded-full px-3 py-2 transition-colors focus:bg-dark-green focus:text-pale-lime-green focus-visible:outline-none"
			>
				<FontAwesomeIcon icon={faArrowLeft} />
				Back
			</button>
			<div className="flex flex-col md:items-center">
				<span
					id={submenuLabelId}
					className="title-h6 flex min-h-[3rem] items-center px-3 py-2"
				>
					{props.label}
				</span>
				<div className="flex flex-col md:items-center" ref={itemAnimation}>
					{props.menuItems?.map((item, index) => {
						return (
							<MobileMenuItem
								isExpanded={props.expandedItem === item.id}
								ref={index === 0 ? firstItem : null}
								key={item.id}
								openSubmenu={props.openSubmenu}
								className="md:ml-[-.5rem]"
								{...item}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default memo(MobileSubmenu);
